/**
 * Visual style fallback data for the frontend.
 *
 * Supabase is the single source of truth for styles. This array is used ONLY
 * as placeholder data while the API query loads (see `fallbackStyles()` in
 * hooks.ts) and for `getStyleOption()` label lookups when a style hasn't been
 * fetched yet. Keep it in sync with the Supabase `public_styles` table.
 *
 * `label` is the English display name (also the fallback when a locale is
 * missing the key). `labelKey` points at the react-i18next key under the
 * `shared` namespace (`styleLabels.<slug>`) used to localize the display name
 * of these BUILT-IN styles. User-created styles have no entry here, so they are
 * never matched by `builtInStyleLabelKey()` and their names always render
 * verbatim (user content is never auto-translated). See
 * `i18n/styleLabels.ts` (`localizeStyleName`).
 *
 * DRIFT NOTE: the public catalog's canonical ids live in the DB (the `_public`
 * owner rows served by `GET /styles`, whose `id` is the slug `style_id`, NOT a
 * UUID — so API ids match the `value`s below). If a catalog style is added to
 * the DB but NOT mirrored here (missing `value`/`labelKey`), it silently falls
 * back to its raw English `name` — the intended, safe behavior (same path as a
 * user-created style). Keep this list + `styleLabels.<slug>` in every UI locale in
 * sync with the DB catalog when styles are added/renamed.
 */

export const VISUAL_STYLES = [
  {
    value: 'bold-manhwa',
    label: 'Bold Manhwa',
    labelKey: 'styleLabels.bold-manhwa',
    tags: ['Korean', 'Illustrative', 'Anime'],
  },
  {
    value: 'chibi-animal-cartoon',
    label: 'Chibi Animal Cartoon',
    labelKey: 'styleLabels.chibi-animal-cartoon',
    tags: ['Cute Chibi', 'Eastern'],
  },
  {
    // Live-action default style. Tags mirror the dev/prod `_public` DB rows
    // exactly (no STYLE_GROUPS value → groups under "Other" in the Style
    // Library, same as the live dev/prod catalog). Do not invent a group tag
    // here: this static entry is a fallback shown only when the API catalog is
    // unavailable, so it should match the live rows it substitutes for
    // (fallback/live drift). prod-meta's row additionally carries "Live Action
    // Cinema" and is served by the live API there.
    value: 'cinematic-real-person',
    // i18n-exempt: English static fallback for the built-in style; localized at render via labelKey + styleLabels.{en,ko,vi}
    label: 'Cinematic Real Person',
    labelKey: 'styleLabels.cinematic-real-person',
    tags: ['Western', 'Pop Culture'],
  },
  {
    value: 'detective-conan',
    label: 'Detective Conan',
    labelKey: 'styleLabels.detective-conan',
    tags: ['Anime', 'Pop Culture', 'Eastern'],
  },
  {
    value: 'elegant-cinematic-3d',
    label: 'Elegant Cinematic 3D',
    labelKey: 'styleLabels.elegant-cinematic-3d',
    tags: ['3D', 'Eastern'],
  },
  {
    value: 'flat-vector-pop',
    label: 'Flat Vector Pop',
    labelKey: 'styleLabels.flat-vector-pop',
    tags: ['Illustrative', 'Pop Culture'],
  },
  {
    value: 'high-fidelity-anime',
    label: 'High-Fidelity Anime',
    labelKey: 'styleLabels.high-fidelity-anime',
    tags: ['Anime', 'Illustrative', 'Pop Culture'],
  },
  {
    value: 'neon-cel-shade',
    label: 'Neon Cel-Shade',
    labelKey: 'styleLabels.neon-cel-shade',
    tags: ['Illustrative', 'Pop Culture'],
  },
  {
    value: 'nostalgic-detective-anime',
    label: 'Nostalgic Detective Anime',
    labelKey: 'styleLabels.nostalgic-detective-anime',
    tags: ['Anime', 'Pop Culture', 'Illustrative'],
  },
  {
    value: 'pixar-3d-animation',
    label: 'Pixar 3D Animation',
    labelKey: 'styleLabels.pixar-3d-animation',
    tags: ['3D', 'Western', 'Pop Culture'],
  },
  {
    value: 'pixar-like-3d',
    label: 'Pixar-Like 3D',
    labelKey: 'styleLabels.pixar-like-3d',
    tags: ['3D', 'Western', 'Pop Culture'],
  },
  {
    value: 'retro-geometric-nocturne',
    label: 'Retro Geometric Nocturne',
    labelKey: 'styleLabels.retro-geometric-nocturne',
    tags: ['Illustrative', 'Western'],
  },
  {
    value: 'romance-fantasy',
    label: 'Romance Fantasy',
    labelKey: 'styleLabels.romance-fantasy',
    tags: ['Korean', 'Illustrative'],
  },
  {
    value: 'soft-watercolor',
    label: 'Soft Watercolor',
    labelKey: 'styleLabels.soft-watercolor',
    tags: ['Illustrative'],
  },
  {
    value: 'stylized-pop-cgi',
    label: 'Stylized Pop CGI',
    labelKey: 'styleLabels.stylized-pop-cgi',
    tags: ['3D', 'Korean', 'Pop Culture'],
  },
  {
    value: 'vintage-storybook',
    label: 'Vintage Storybook',
    labelKey: 'styleLabels.vintage-storybook',
    tags: ['Illustrative', 'Western'],
  },
]

export type VisualStyle = (typeof VISUAL_STYLES)[number]['value'] | string

// Short Form Drama beta positioning favors a real-person / live-action cinematic look.
// Flipped from the deprecated "Elegant Cinematic 3D" (3D CGI) to "Cinematic Real Person"
// (surfaced as "Naturalistic Cinema" on prod-meta).
export const DEFAULT_STYLE: VisualStyle = 'cinematic-real-person'

// Cross-language contract: keep in sync with
// agentic_workflow.core.style_library.NO_STYLE_ID.
export const NO_STYLE_ID: VisualStyle = 'NO_STYLE'
export const NO_STYLE_OPTION = {
  value: NO_STYLE_ID,
  // Internal fallback only; render through labelKey for user-facing copy.
  label: NO_STYLE_ID,
  labelKey: 'noStyleOption',
  tags: ['Prompt Only'],
}

/**
 * Tag values that can be assigned to styles. These are the actual enum values
 * stored on style objects in `style.json` and returned by the API.
 *
 * NOTE: these are the LEGACY cross-cutting descriptors. As of the style-group
 * migration they are being retired in favour of STYLE_GROUPS (one group per
 * style). Kept exported for back-compat with any code still referencing the
 * old vocabulary, but they no longer drive the Style Library filter bar.
 */
export const STYLE_TAGS = [
  'Pop Culture',
  'Anime',
  'Illustrative',
  'Korean',
  'Western',
  '3D',
  'Eastern',
  'Cute Chibi',
]

/**
 * Style groups — the overarching style category (the "Style" column in the
 * styles checklist sheet). Each style belongs to exactly ONE group, stored as
 * one of these values inside the style's `tags` array (no schema change).
 *
 * These drive BOTH the Style Library filter pills and the per-group section
 * titles in the "All" view. Order here is the display order until an admin
 * tool manages it explicitly; `STYLE_GROUP_FALLBACK` catches any style with no
 * recognized group value yet.
 */
export const STYLE_GROUPS = [
  'Live Action Cinema',
  '3D Animation',
  '2D Animation',
  'Stop Motion',
  'Illustration',
]

export const STYLE_GROUP_SET = new Set<string>(STYLE_GROUPS)

/** Section title for public styles that have no recognized group yet. */
export const STYLE_GROUP_FALLBACK = 'Other'

/**
 * Virtual filter tab for user-curated favourites (Workplace post
 * 1556548202769144). Like `All`, this is NOT a data group stored in a style's
 * `tags` — it is resolved against the user's favourites set at render time and
 * only shown to authenticated users. Kept out of `STYLE_GROUPS` on purpose.
 */
export const STYLE_FAVOURITES = 'Favourites'

/**
 * react-i18next label keys (under the `shared` namespace) for the group
 * *display* text — the filter pills and the "All"-view section titles.
 *
 * IMPORTANT: the STYLE_GROUPS *values* above are NOT display strings — they are
 * (a) the filter identity (`activeTag`), (b) the data-tag matcher against a
 * style's `tags[]`, and (c) the stable `data-testid` slug. They must stay in
 * this fixed English vocabulary. What renders to the user is the *label*, which
 * is localized through these keys — mirroring the built-in style-name pattern
 * (`builtInStyleLabelKey` → `styleLabels.<slug>` → `localizeStyleName`).
 *
 * Keyed by group value; the special `All` filter and the `Other` fallback have
 * their own keys. Keep this map + the `styleGroups.<slug>` keys in every UI locale in
 * lockstep (the source/target locale parity gate enforces the locale side). A missing entry
 * falls back to the raw group value via `defaultValue` in `localizeStyleGroup`.
 */
export const STYLE_GROUP_ALL = 'All'

export const STYLE_GROUP_LABEL_KEYS: Record<string, string> = {
  [STYLE_GROUP_ALL]: 'styleGroups.all',
  'Live Action Cinema': 'styleGroups.live-action-cinema',
  '3D Animation': 'styleGroups.3d-animation',
  '2D Animation': 'styleGroups.2d-animation',
  'Stop Motion': 'styleGroups.stop-motion',
  'Illustration': 'styleGroups.illustration',
  [STYLE_FAVOURITES]: 'styleGroups.favourites',
  [STYLE_GROUP_FALLBACK]: 'styleGroups.other',
}

/**
 * Resolve a style-group value (or the `All` filter / `Other` fallback) to its
 * react-i18next label key under the `shared` namespace, or `undefined` when the
 * value is not a recognized group. Consumers must render the raw value
 * unchanged when this returns `undefined`. See `i18n/styleLabels.ts`
 * (`localizeStyleGroup`).
 */
export function styleGroupLabelKey(group: string): string | undefined {
  return STYLE_GROUP_LABEL_KEYS[group]
}

/**
 * Filter tabs shown in the Style Library modal.
 * "All" is a virtual filter (shows every group, titled). The remaining tabs
 * are the style groups — selecting one narrows to that single group.
 */
export const STYLE_FILTER_TABS = [
  'All',
  ...STYLE_GROUPS,
]

/**
 * Tabs that are disabled (not yet implemented). None currently.
 */
export const DISABLED_FILTER_TABS = new Set<string>([])

/**
 * Get style option by value
 */
export function getStyleOption(value: string) {
  return VISUAL_STYLES.find((s) => s.value === value)
}

/**
 * Resolve a style id to its react-i18next label key (under the `shared`
 * namespace), or `undefined` when the id is not a built-in style.
 *
 * The `undefined` return is the structural guard that keeps user-created style
 * names (and any unknown/new DB slug) from being auto-translated: only built-in
 * slugs declared in `VISUAL_STYLES` carry a `labelKey`. Consumers must render
 * the raw name unchanged when this returns `undefined`. See
 * `i18n/styleLabels.ts` (`localizeStyleName`).
 */
export function builtInStyleLabelKey(id: string): string | undefined {
  if (id === NO_STYLE_ID) {
    return NO_STYLE_OPTION.labelKey
  }
  return VISUAL_STYLES.find((s) => s.value === id)?.labelKey
}
